import React, { createContext, memo, useEffect, useRef, useState, useContext, useMemo } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const MicroFrontendContext = createContext({
    microFrontends: {},
    load: () => Promise.resolve({}),
    render: () => { },
    destroy: () => { },
});

function shallowEqual(objA, objB) {
    if (objA === objB) {
        return true;
    }
    if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
        return false;
    }
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);
    if (keysA.length !== keysB.length) {
        return false;
    }
    // Test for A's keys different from B.
    const bHasOwnProperty = Object.prototype.hasOwnProperty.bind(objB);
    for (let i = 0; i < keysA.length; i++) {
        if (!bHasOwnProperty(keysA[i]) || objA[keysA[i]] !== objB[keysA[i]]) {
            return false;
        }
    }
    return true;
}

/**
 *
 * `MicroFrontendRenderBarrier` has a strict and controlled rendering procedure to avoid
 * breaking the micro-frontend by accidentally flushing its DOM elements.
 *
 * The controlled renders always happen in this fixed order:
 *    1. Two different renders can happen depending on the platform:
 *       - If Node: render the <edge:fragment /> markup.
 *       - If browser: render the SSR version to satisfy the hydration process OR render
 *         the fallback component for dynamically resolved micro-frontends
 *    2. Render the container div (with the container id) once the scripts of the
 *       micro-frontend are loaded. The container div still has the SSR version as
 *       content which the client-side React application can hydrate.
 *
 * No further re-renders should happen throughout the lifetime of this component.
 *
 */
const MicroFrontendRenderBarrier = ({ containerId, microFrontend: { name, url, clusterUrl, loaded, artifact }, clientSideProps, children: fallbackChildren, }) => {
    useEffect(() => {
        if (!containerId) {
            return () => { };
        }
        const type = containerId;
        const handleMicroFrontendEvent = (event) => {
            const microFrontendEvent = event.detail;
            if (microFrontendEvent.type === 'ROOT_CONTAINER_PROPS_CHANGED') {
                const container = document.getElementById(containerId);
                if (container) {
                    // todo: allow more props
                    container.className = microFrontendEvent.props.className;
                }
            }
        };
        window.addEventListener(type, handleMicroFrontendEvent);
        return () => {
            window.removeEventListener(type, handleMicroFrontendEvent);
        };
    }, [containerId]);
    if (!process.browser && process.env.NODE_ENV === 'production') {
        const params = new URLSearchParams({
            props: JSON.stringify(clientSideProps || {}),
        });
        return React.createElement('fragment', {
            src: `${clusterUrl || url}?${params.toString()}`,
        });
    }
    if (!loaded || !containerId) {
        // The artifact can actually be falsy here, since it is possible that a micro-frontend was
        // dynamically referenced (ie. not present in SSR) and an artifact has yet to be resolved.
        // In this case we provide a possibility for a fallback component, such as a loading indicator.
        if (!artifact) {
            return React.createElement(React.Fragment, null, fallbackChildren);
        }
        return React.createElement(artifact.rootElement.tagName, {
            // todo: allow more attributes
            className: artifact.rootElement.attributes.class,
            dangerouslySetInnerHTML: { __html: artifact.rootElementChildrenHtml },
        });
    }
    if (!artifact) {
        console.error(`Attempted to render application ${name}, but no artifact was present.`);
        return null;
    }
    return React.createElement(artifact.rootElement.tagName, {
        // todo: allow more attributes
        className: artifact.rootElement.attributes.class,
        id: containerId,
        dangerouslySetInnerHTML: { __html: artifact.rootElementChildrenHtml },
    });
};
var MicroFrontendRenderBarrier$1 = memo(MicroFrontendRenderBarrier, (prevProps, nextProps) => {
    // The container component should only update once in its lifetime, and that
    // happens when both the entrypoint scripts are loaded and the container id is
    // resolved (ie. the micro-frontend is ready to be hydrated on the client side).
    if ((prevProps.microFrontend.loaded !== nextProps.microFrontend.loaded ||
        prevProps.containerId !== nextProps.containerId) &&
        nextProps.microFrontend.loaded &&
        nextProps.containerId) {
        return false;
    }
    if (nextProps.containerId &&
        !shallowEqual(prevProps.clientSideProps, nextProps.clientSideProps)) {
        const event = {
            type: 'CLIENT_SIDE_PROPS_CHANGED',
            props: nextProps.clientSideProps,
        };
        window.dispatchEvent(new CustomEvent(nextProps.containerId, {
            detail: event,
        }));
    }
    return true;
});

const withMicroFrontend = (name) => {
    // This is a wrapper component over the micro-frontend instance. It controls the
    // lifecycle of the micro-frontend instance and - more importantly -, shields it from the
    // unavoidable re-render caused by the MicroFrontendContext context update.
    const Component = (_a) => {
        var { children: fallbackChildren } = _a, props = __rest(_a, ["children"]);
        const [containerId, setContainerId] = useState('');
        const microFrontendContext = useContext(MicroFrontendContext);
        const microFrontend = microFrontendContext.microFrontends[name];
        useEffect(() => {
            const load = () => __awaiter(void 0, void 0, void 0, function* () {
                const { containerId } = yield microFrontendContext.load(name, props);
                if (containerId) {
                    setContainerId(containerId);
                }
            });
            // Start loading the scripts of the micro-frontend AFTER first render. The first
            // render is reserved to the SSR markup so that the hydration warning is silenced.
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            load();
        }, []);
        useEffect(() => {
            // Start rendering the micro-frontend AFTER it has been successfully loaded. See the hook above.
            if ((microFrontend === null || microFrontend === void 0 ? void 0 : microFrontend.loaded) && containerId) {
                microFrontendContext.render(name, containerId, props);
                return () => microFrontendContext.destroy(name, containerId);
            }
            return () => { };
        }, [microFrontend === null || microFrontend === void 0 ? void 0 : microFrontend.loaded, containerId]);
        if (!microFrontend) {
            return null;
        }
        return (React.createElement(MicroFrontendRenderBarrier$1, { containerId: containerId, microFrontend: microFrontend, clientSideProps: props }, fallbackChildren));
    };
    Component.displayName = `MicroFrontendContainer(${name})`;
    return Component;
};
const useMicroFrontend = (...names) => {
    const microFrontendRef = useRef(names.reduce((prev, curr) => (Object.assign(Object.assign({}, prev), { [curr]: withMicroFrontend(curr) })), {}));
    return microFrontendRef.current;
};

const loadScript = (scriptUrl, onScriptLoaded) => {
    if (document.querySelector(`[src='${scriptUrl.toString()}']`)) {
        // The script is already loaded, we are good to go!
        return;
    }
    const script = document.createElement('script');
    script.src = scriptUrl.toString();
    script.onload = onScriptLoaded;
    document.body.appendChild(script);
};
const createMicroFrontendRegistryEntry = (name, url, clusterUrl) => {
    var _a;
    const microFrontendHost = new URL(url).host;
    const artifact = process.browser
        ? (_a = window.__MICRO_FRONTEND_ARTIFACTS__) === null || _a === void 0 ? void 0 : _a[microFrontendHost]
        : undefined;
    return {
        name,
        artifact,
        loaded: false,
        url,
        clusterUrl,
        renderer: {
            mount: (containerId, serverSideProps, clientSideProps) => {
                var _a, _b;
                const artifact = (_a = window.__MICRO_FRONTEND_ARTIFACTS__) === null || _a === void 0 ? void 0 : _a[microFrontendHost];
                if (!artifact) {
                    return;
                }
                (_b = window.__MICRO_FRONTEND_FACTORIES__) === null || _b === void 0 ? void 0 : _b[artifact.id].mount(containerId, serverSideProps, clientSideProps);
            },
            unmount: (containerId) => {
                var _a, _b;
                const artifact = (_a = window.__MICRO_FRONTEND_ARTIFACTS__) === null || _a === void 0 ? void 0 : _a[microFrontendHost];
                if (!artifact) {
                    return;
                }
                (_b = window.__MICRO_FRONTEND_FACTORIES__) === null || _b === void 0 ? void 0 : _b[artifact.id].unmount(containerId);
            },
        },
    };
};
const checkAndWarnDuplicateRegistryEntries = (registry, newEntry) => {
    if (newEntry.artifact) {
        const existingEntry = Object.values(registry).find(({ artifact }) => { var _a; return (artifact === null || artifact === void 0 ? void 0 : artifact.id) === ((_a = newEntry.artifact) === null || _a === void 0 ? void 0 : _a.id); });
        if (existingEntry) {
            const { name: existingName } = existingEntry;
            console.error(`Warning: Micro-frontend '${newEntry.name}' is already registered under the name '${existingName}'.`);
        }
    }
};
const MicroFrontendProvider = ({ microFrontends: registeredMicroFrontends, children, }) => {
    // Keep the counter and the loaded entrypoints in a ref as they do not need invoke a re-render on change.
    const counterRef = useRef(0);
    const loadedEntrypoints = useRef({});
    const createContainerId = (name) => `${name}-${++counterRef.current}`;
    const [microFrontends, setMicroFrontends] = useState(() => {
        // Convert the list of registered micro-frontends into registry entries.
        const registry = {};
        // eslint-disable-next-line no-restricted-syntax
        for (const [name, url] of Object.entries(registeredMicroFrontends)) {
            const publicUrl = typeof url === 'string' ? url : url.url;
            const clusterUrl = typeof url !== 'string' ? url.clusterUrl : undefined;
            const entry = createMicroFrontendRegistryEntry(name, publicUrl, clusterUrl);
            // Notify the user when a micro-frontend entry with this ID already exists.
            checkAndWarnDuplicateRegistryEntries(registry, entry);
            registry[name] = entry;
        }
        return registry;
    });
    const contextState = useMemo(() => ({
        microFrontends,
        load: (name, clientSideProps) => __awaiter(void 0, void 0, void 0, function* () {
            if (!process.browser) {
                console.error(`Micro-frontend ${name} entrypoints can't be loaded on the server side.`);
            }
            const microFrontend = microFrontends[name];
            if (!microFrontend) {
                console.error(`Micro-frontend ${name} can't be loaded since it was not registered.`);
                return {};
            }
            if (microFrontend.loaded) {
                return {
                    containerId: createContainerId(name),
                };
            }
            let artifact = microFrontend.artifact;
            if (!artifact) {
                // If there is no artifact present, it is possible that the micro-frontend was dynamically loaded and
                // thus no artifact came back in the SSR response. We try and resolve this here by loading the artifact.
                const params = new URLSearchParams({
                    artifact: '',
                    props: JSON.stringify(clientSideProps || {}),
                });
                // TODO: multiple dynamically loaded instances of the same micro-frontend launch separate requests.
                const response = yield fetch(`${microFrontend.url}?${params.toString()}`);
                if (response.ok) {
                    artifact = yield response.json();
                    if (artifact) {
                        // Add artifact to the global store.
                        if (!window.__MICRO_FRONTEND_ARTIFACTS__) {
                            window.__MICRO_FRONTEND_ARTIFACTS__ = {};
                        }
                        const microFrontendHost = new URL(microFrontend.url).host;
                        window.__MICRO_FRONTEND_ARTIFACTS__[microFrontendHost] = artifact;
                        // Add artifact to the micro-frontend registry.
                        setMicroFrontends((prevState) => {
                            const entry = Object.assign(Object.assign({}, prevState[name]), { // Bang: the micro-frontend exists here for sure.
                                artifact });
                            // Notify the user when a micro-frontend entry with this ID already exists.
                            checkAndWarnDuplicateRegistryEntries(prevState, entry);
                            return Object.assign(Object.assign({}, prevState), { [name]: entry });
                        });
                    }
                }
                else {
                    console.error(`Could not dynamically fetch artifact for micro-frontend ${name}.`, response.status);
                    return {};
                }
            }
            if (!artifact) {
                console.error(`Micro-frontend ${name} cannot be rendered as it is has no artifact.`);
                return {};
            }
            // eslint-disable-next-line no-restricted-syntax
            for (const entrypoint of artifact.entrypoints) {
                loadScript(new URL(entrypoint, artifact.appUrl), () => {
                    setMicroFrontends((prevState) => {
                        // Bang: the micro-frontend will surely exist in the registry at this point.
                        const prevMicroFrontendState = prevState[name];
                        const prevMicroFrontendArtifact = prevMicroFrontendState.artifact;
                        // Mark the entrypoint as loaded.
                        if (!loadedEntrypoints.current[prevMicroFrontendArtifact.id]) {
                            loadedEntrypoints.current[prevMicroFrontendArtifact.id] = [entrypoint];
                        }
                        else {
                            loadedEntrypoints.current[prevMicroFrontendArtifact.id].push(entrypoint);
                        }
                        const loadedAppEntrypoints = loadedEntrypoints.current[prevMicroFrontendArtifact.id];
                        return Object.assign(Object.assign({}, prevState), { [name]: Object.assign(Object.assign({}, prevMicroFrontendState), { loaded: prevMicroFrontendArtifact.entrypoints.every((e) => loadedAppEntrypoints.includes(e)) }) });
                    });
                });
            }
            return {
                containerId: createContainerId(name),
            };
        }),
        render: (name, containerId, clientSideProps) => {
            const microFrontend = microFrontends[name];
            if (!microFrontend) {
                console.error(`Micro-frontend ${name} cannot be rendered as it is not registered.`);
                return;
            }
            if (!microFrontend.artifact) {
                console.error(`Micro-frontend ${name} cannot be rendered as it is has no artifact.`);
                return;
            }
            microFrontend.renderer.mount(containerId, microFrontend.artifact.serverSideProps, clientSideProps);
        },
        destroy: (name, containerId) => {
            const microFrontend = microFrontends[name];
            if (!microFrontend) {
                console.error(`Micro-frontend ${name} cannot be destroyed as it is not registered.`);
                return;
            }
            microFrontend.renderer.unmount(containerId);
        },
    }), [microFrontends]);
    return (React.createElement(MicroFrontendContext.Provider, { value: contextState }, children));
};
var MicroFrontendProvider$1 = memo(MicroFrontendProvider);

export { MicroFrontendProvider$1 as MicroFrontendProvider, useMicroFrontend };
//# sourceMappingURL=index.js.map
